import { useState, useEffect } from 'react'
import { useLatest } from '../useLatest'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useBruktbilFilters } from '../useBruktbilFilters'
import { useExplanationFilterLines } from '../explanationFilters'
import TopMerkerTable from '../TopMerkerTable'
import TopModellerTable from '../TopModellerTable'
import AlderStatsGrid from '../AlderStatsGrid'
import DrivstoffMatrise from '../DrivstoffMatrise'
import AldersprofilChart, { fromKjoperAlderSeries } from '../AldersprofilChart'
import MerkeMultiSelect from '../../../components/MerkeMultiSelect'
import {
  fetchOrgMerker, fetchOrgModeller, fetchOrgKlasser, fetchOrgAldersprofil,
  fetchOrgKjoperalder, fetchOrgDrivstoff,
  type OrgMerkerResponse, type OrgModellerResponse, type OrgKlasserResponse,
  type OrgAldersprofilResponse, type OrgKjoperAlderResponse, type OrgDrivstoffResponse,
} from './api'
import QueryExplanationBox from '../../../components/QueryExplanationBox'
import { useMaxDataDate } from '../../../hooks/useMaxDataDate'

type Side = 'kjopere' | 'selgere'
type Tab = 'merker' | 'modeller' | 'klasser' | 'aldersprofil' | 'kjoperalder' | 'drivstoff'

// The two age tabs and Drivstofftype share the brand filter, so the pick
// carries across them (#586 AC5).
const MERKE_TABS: Tab[] = ['aldersprofil', 'kjoperalder', 'drivstoff']

const TABS: { slug: Tab; labelKey: `detaljTabs.${Tab}` }[] = [
  { slug: 'merker', labelKey: 'detaljTabs.merker' },
  { slug: 'modeller', labelKey: 'detaljTabs.modeller' },
  { slug: 'klasser', labelKey: 'detaljTabs.klasser' },
  { slug: 'aldersprofil', labelKey: 'detaljTabs.aldersprofil' },
  { slug: 'kjoperalder', labelKey: 'detaljTabs.kjoperalder' },
  { slug: 'drivstoff', labelKey: 'detaljTabs.drivstoff' },
]

const _nb = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 1 })

interface Props { side: Side }

export default function OrgDetaljerPage({ side }: Props) {
  const { t } = useTranslation('bruktbil')
  const { orgNr } = useParams<{ orgNr: string }>()
  const filters = useBruktbilFilters()
  const [searchParams] = useSearchParams()
  const maxDataDate = useMaxDataDate()
  const filterLines = useExplanationFilterLines()
  const [tab, setTab] = useState<Tab>('merker')

  const [merker, setMerker] = useState<OrgMerkerResponse | null>(null)
  const [modeller, setModeller] = useState<OrgModellerResponse | null>(null)
  const [klasser, setKlasser] = useState<OrgKlasserResponse | null>(null)
  const [aldersprofil, setAldersprofil] = useState<OrgAldersprofilResponse | null>(null)
  // Brand filter for the Alder-profil tab (#482).
  const [merkeFilter, setMerkeFilter] = useState<string[]>([])
  const [merkeOptions, setMerkeOptions] = useState<string[]>([])
  const [kjoperalder, setKjoperalder] = useState<OrgKjoperAlderResponse | null>(null)
  const [drivstoff, setDrivstoff] = useState<OrgDrivstoffResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const f = {
    from: filters.from, to: filters.to, klasse: filters.klasse,
    varebil: filters.varebil, omregistrering: filters.omregistrering,
    selgerPostnummer: filters.selgerPostnummer, kjoperPostnummer: filters.kjoperPostnummer,
  }

  useLatest(alive => {
    if (!orgNr) return
    setLoading(true)
    setMerker(null); setModeller(null); setKlasser(null); setAldersprofil(null); setDrivstoff(null)
    const done = () => { if (alive()) setLoading(false) }
    if (tab === 'merker') fetchOrgMerker(side, orgNr, f).then(r => { if (alive()) setMerker(r.data) }).finally(done)
    else if (tab === 'modeller') fetchOrgModeller(side, orgNr, f).then(r => { if (alive()) setModeller(r.data) }).finally(done)
    else if (tab === 'klasser') fetchOrgKlasser(side, orgNr, f).then(r => { if (alive()) setKlasser(r.data) }).finally(done)
    else if (tab === 'aldersprofil')
      fetchOrgAldersprofil(side, orgNr, f, merkeFilter)
        .then(r => { if (alive()) setAldersprofil(r.data) }).finally(done)
    else if (tab === 'kjoperalder') setLoading(false)   // fetched by the always-on effect below
    // Drivstoff is the fall-through: every other tab is named above, so a new
    // one must add its own branch rather than land here silently.
    else fetchOrgDrivstoff(side, orgNr, f, merkeFilter).then(r => { if (alive()) setDrivstoff(r.data) }).finally(done)
  }, [orgNr, tab, side, JSON.stringify(merkeFilter), filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // Brand options are independent of the brand selection, so they load in
  // their own effect. Refetching them on every pick reset the list mid-use.
  useLatest(alive => {
    if (!orgNr || !MERKE_TABS.includes(tab)) return
    fetchOrgMerker(side, orgNr, f)
      .then(r => { if (alive()) setMerkeOptions(r.data.rows.map(x => x.merke)) })
      .catch(() => { if (alive()) setMerkeOptions([]) })
  }, [orgNr, tab, side, filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // Kjøperalder loads regardless of the active tab: whether the tab exists at
  // all depends on has_private_buyers, which only the response can tell us
  // (#485 AC2). It doubles as the tab's own data, so opening it costs nothing.
  useLatest(alive => {
    if (!orgNr) return
    fetchOrgKjoperalder(side, orgNr, f, merkeFilter)
      .then(r => { if (alive()) setKjoperalder(r.data) })
      .catch(() => { if (alive()) setKjoperalder(null) })
  }, [orgNr, side, JSON.stringify(merkeFilter), filters.from, filters.to, JSON.stringify(filters.klasse), filters.varebil, filters.omregistrering, filters.selgerPostnummer, filters.kjoperPostnummer])

  // A filter change can empty the segment of private buyers while the tab is
  // open — fall back rather than leave a selected tab with nothing behind it.
  const hideKjoperalder = kjoperalder != null && !kjoperalder.has_private_buyers
  useEffect(() => {
    if (hideKjoperalder && tab === 'kjoperalder') setTab('merker')
  }, [hideKjoperalder, tab])

  const visibleTabs = TABS.filter(tab => tab.slug !== 'kjoperalder' || !hideKjoperalder)

  const backLabel = side === 'selgere' ? t('orgDetaljer.tilbakeSelgere') : t('orgDetaljer.tilbakeKjopere')
  const backUrl = `/bruktbil/${side}?${searchParams.toString()}`
  const vehicleFilter = filters.varebil === false ? 'personbil' : filters.varebil === true ? 'varebil' : 'alle'

  return (
    <div className="space-y-4">
      <Link to={backUrl} className="inline-flex items-center text-sm text-primary-600 dark:text-primary-400 hover:underline">
        ← {backLabel}
      </Link>

      <div className="card p-4">
        <p className="text-xs text-gray-500 dark:text-gray-400 font-mono">{orgNr}</p>
      </div>

      <QueryExplanationBox
        maxDataDate={maxDataDate}
        // An explicitly chosen period is already exactly what the user asked
        // for, so show it verbatim; only the implicit default runs to today,
        // past where data goes, and needs clipping to the cutoff (#594 AC4).
        outputMode={filters.periodExplicit ? 'custom' : 'year_to_date'}
        vehicleFilter={vehicleFilter as 'personbil' | 'varebil' | 'alle'}
        startDate={filters.from}
        endDate={filters.to}
        periodLabel={t('oversikt.valgtPeriode')}
        filterLines={filterLines}
      />

      <div className="flex border-b border-gray-200 dark:border-dark-border overflow-x-auto">
        {visibleTabs.map(vt => (
          <button key={vt.slug} onClick={() => setTab(vt.slug)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
              tab === vt.slug
                ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
            }`}>
            {t(vt.labelKey)}
          </button>
        ))}
      </div>

      {/* Merke-filter stays mounted while the profile reloads: selecting a brand
          flips `loading`, and unmounting here would close the dropdown after
          every single pick. */}
      {MERKE_TABS.includes(tab) && (
        <div className="card p-4">
          <MerkeMultiSelect
            options={merkeOptions}
            selected={merkeFilter}
            onChange={setMerkeFilter}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {tab === 'drivstoff' ? t('detaljer.merkeHintDrivstoff') : t('detaljer.merkeHintAlder')}
          </p>
        </div>
      )}

      {loading && <div className="card p-4 animate-pulse h-40 bg-gray-100 dark:bg-dark-border rounded-sm" />}

      {/* Merker */}
      {!loading && tab === 'merker' && merker && (
        <TopMerkerTable rows={merker.rows} />
      )}

      {/* Modeller tab — one row per merke+modell pair (#586 AC2) */}
      {!loading && tab === 'modeller' && modeller && (
        <TopModellerTable rows={modeller.rows} />
      )}

      {/* Klasser */}
      {!loading && tab === 'klasser' && klasser && (
        <div className="card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800 text-left text-xs uppercase tracking-wide text-gray-600 dark:text-gray-400">
              <tr><th className="px-3 py-2">{t('common.klasse')}</th><th className="px-3 py-2 text-right">{t('common.antall')}</th><th className="px-3 py-2 text-right">{t('common.andelPct')}</th></tr>
            </thead>
            <tbody>
              {klasser.rows.map((r, i) => (
                <tr key={r.klasse} className={i % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800/30' : ''}>
                  <td className="px-3 py-1.5 font-mono text-xs">{r.klasse}</td>
                  <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.count)}</td>
                  <td className="px-3 py-1.5 tabular-nums text-right">{_nb.format(r.share_pct)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Aldersprofil biler — 1-year line chart 0–25 + 25+ badge */}
      {!loading && tab === 'aldersprofil' && aldersprofil && (
        <div className="space-y-4">
          <div className="card p-4 flex items-center gap-6">
            <div className="flex gap-8 text-sm">
              <div><p className="text-xs text-gray-500">{t('detaljer.snitt')}</p><p className="font-semibold">{aldersprofil.stats.snittalder != null ? t('units.aar', { value: _nb.format(aldersprofil.stats.snittalder) }) : '—'}</p></div>
              <div><p className="text-xs text-gray-500">{t('detaljer.median')}</p><p className="font-semibold">{aldersprofil.stats.medianalder != null ? t('units.aar', { value: _nb.format(aldersprofil.stats.medianalder) }) : '—'}</p></div>
            </div>
            <div className="ml-auto px-3 py-1.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium">
              {t('detaljer.over25', { n: _nb.format(aldersprofil.over_25 ?? 0) })}
            </div>
          </div>
          <AldersprofilChart series={aldersprofil.series ?? []} />
          <AlderStatsGrid
            series={aldersprofil.series ?? []}
            ageLabel={t('detaljer.bilalder')}
            footnote={t('detaljer.bilalderFootnote')}
          />
        </div>
      )}

      {/* Kjøperalder — privatkjøp only, 18–75 + 75+ badge */}
      {tab === 'kjoperalder' && kjoperalder && (
        <div className="space-y-4">
          <div className="card p-4">
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">{t('orgDetaljer.kunPrivatkjopere')}</p>
            <div className="flex items-center gap-6">
              <div className="flex gap-8 text-sm">
                <div><p className="text-xs text-gray-500">{t('detaljer.snitt')}</p><p className="font-semibold">{kjoperalder.snittalder != null ? t('units.aar', { value: _nb.format(kjoperalder.snittalder) }) : '—'}</p></div>
                <div><p className="text-xs text-gray-500">{t('detaljer.median')}</p><p className="font-semibold">{kjoperalder.medianalder != null ? t('units.aar', { value: _nb.format(kjoperalder.medianalder) }) : '—'}</p></div>
              </div>
              <div className="ml-auto px-3 py-1.5 rounded-sm bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300 text-sm font-medium">
                {t('detaljer.over75', { n: _nb.format(kjoperalder.over_75 ?? 0) })}
              </div>
            </div>
          </div>
          <AldersprofilChart
            series={fromKjoperAlderSeries(kjoperalder.series ?? [])}
            xLabel={t('detaljer.kjoperalderAar')}
          />
          <AlderStatsGrid
            series={kjoperalder.series ?? []}
            ageLabel={t('detaljer.kjoperalder')}
            footnote={t('detaljer.kjoperalderFootnote')}
          />
        </div>
      )}

      {/* Drivstoff — raw SVV values, optionally split by merke (#586 AC5) */}
      {!loading && tab === 'drivstoff' && drivstoff && (
        <DrivstoffMatrise series={drivstoff.series ?? []} />
      )}
    </div>
  )
}
